import { z } from 'zod'

/**
 * Papéis do Hub SISTEMAS.
 *
 * Papel ≠ cargo. Cargo é organizacional; papel é permissão.
 * Ver docs/SDD/02-personas-e-rbac.md
 *
 * INVIOLÁVEL (I7): `finance_admin` NÃO é implicado por `platform_admin`.
 * Custo de pessoas exige concessão explícita (D11, ADR-0004).
 *
 * `budget_operator` (ADR-0006) existe porque lançar recibo do budget e ver
 * salário do time eram a mesma chave, e não são a mesma coisa: quem opera a
 * despesa do departamento não precisa da folha.
 */
export const ROLES = [
  'platform_admin',
  'gestor',
  'lider',
  'membro',
  'leitor',
  'finance_admin',
  'budget_operator',
  'infra',
] as const

export const roleSchema = /* @__PURE__ */ z.enum(ROLES)
export type Role = z.infer<typeof roleSchema>

/**
 * Papéis avaliados por projeto. Os demais são globais.
 * A mesma pessoa pode ser `lider` no Projeto A e `membro` no Projeto B.
 */
export const PROJECT_SCOPED_ROLES = ['lider', 'membro'] as const
export type ProjectScopedRole = (typeof PROJECT_SCOPED_ROLES)[number]

export function isProjectScoped(role: Role): role is ProjectScopedRole {
  return (PROJECT_SCOPED_ROLES as readonly string[]).includes(role)
}

/**
 * Hierarquia de LEITURA. Escrita nunca é herdada — cada ação é avaliada
 * individualmente na matriz de permissões.
 */
const READ_RANK: Record<Role, number> = {
  platform_admin: 50,
  gestor: 40,
  lider: 30,
  membro: 20,
  leitor: 10,
  // Papéis laterais: concedem acesso a um domínio específico, não a um nível.
  finance_admin: 0,
  budget_operator: 0,
  infra: 0,
}

export function readRank(role: Role): number {
  return READ_RANK[role]
}

export function highestReadRank(roles: readonly Role[]): number {
  return roles.reduce((max, role) => Math.max(max, readRank(role)), 0)
}

/**
 * Papéis laterais não participam da hierarquia: são chaves de domínio.
 * Ter `finance_admin` não dá nenhum poder fora do financeiro, e nenhum
 * outro papel concede financeiro por herança.
 */
export const LATERAL_ROLES = ['finance_admin', 'budget_operator', 'infra'] as const
export type LateralRole = (typeof LATERAL_ROLES)[number]

export function isLateral(role: Role): role is LateralRole {
  return (LATERAL_ROLES as readonly string[]).includes(role)
}

/**
 * Rótulo curto de cada papel, para badges e rodapé. A UI nunca mostra o
 * valor cru (`platform_admin`); a descrição longa de cada papel, usada no
 * formulário de acesso, fica no módulo de pessoas.
 */
export const ROLE_LABEL: Record<Role, string> = {
  platform_admin: 'Admin da plataforma',
  gestor: 'Gestor',
  lider: 'Líder',
  membro: 'Membro',
  leitor: 'Leitor',
  finance_admin: 'Financeiro',
  budget_operator: 'Budget',
  infra: 'Infra',
}

/**
 * O papel que apresenta a pessoa quando só cabe um: o de maior alcance na
 * hierarquia de leitura. Papéis laterais só aparecem quando são os únicos —
 * "Financeiro" não descreve alguém que também é gestor.
 */
const PRESENTATION_ORDER: readonly Role[] = [
  'platform_admin',
  'gestor',
  'lider',
  'membro',
  'leitor',
  'finance_admin',
  'budget_operator',
  'infra',
]

export function primaryRole(roles: readonly Role[]): Role | null {
  for (const role of PRESENTATION_ORDER) {
    if (roles.includes(role)) return role
  }
  return null
}

export function primaryRoleLabel(roles: readonly Role[]): string {
  const role = primaryRole(roles)
  return role ? ROLE_LABEL[role] : 'Sem papel'
}

/**
 * A fronteira financeira, partida em duas (ADR-0006).
 *
 * Até aqui `can_access_finance()` cobria `person_cost` E `budget_month`/
 * `expense`: conceder o papel para alguém lançar recibo abria a folha junto.
 * São dois domínios com públicos diferentes, então são duas chaves.
 *
 * INVIOLÁVEL (I7/D11): custo de pessoas continua sendo `finance_admin` e só.
 * Nem `platform_admin`, nem `gestor`, nem `budget_operator` chegam à folha.
 */
export function canAccessPersonCost(roles: readonly Role[]): boolean {
  return roles.includes('finance_admin')
}

/**
 * Budget do departamento: teto mensal, lançamentos, recibos e relatório.
 * `finance_admin` continua entrando — o papel não encolheu; ganhou um vizinho
 * mais estreito para quem só opera a despesa.
 */
export function canAccessBudget(roles: readonly Role[]): boolean {
  return roles.includes('finance_admin') || roles.includes('budget_operator')
}

/**
 * Papéis cuja concessão exige justificativa registrada. Espelha o trigger
 * `member_role_require_reason` — a UI pede o texto, o banco recusa sem ele.
 */
export const SENSITIVE_ROLES: readonly Role[] = [
  'platform_admin',
  'finance_admin',
  'budget_operator',
]
